import { z } from 'zod';
/**
 * Automation rule — operator-defined "When X → Then Y" entry the agent
 * evaluates against the connected-tool graph. Grouped by category in
 * the `/rules` surface (Briefing cadence · Alert routing · Auto-drafts ·
 * Escalation).
 *
 * Phase 5 ships the schema + CRUD wire + UI; the rules engine that
 * actually fires on signals is a follow-up. The `whenCondition` /
 * `thenAction` are free-text today (the operator writes them in plain
 * English); a structured DSL replaces the strings once the agent learns
 * to evaluate them — the row shape stays stable across that migration
 * because the natural-language strings remain the canonical display.
 */
export const AUTOMATION_RULES_COLLECTION = 'automationRules';
/**
 * Four operator-recognizable buckets shown as separate cards in the
 * `/rules` surface. Keep this enum in lockstep with the i18n
 * `rules.category.<slug>.{title,sub,empty}` keys + the page's render
 * order — promoting a new category requires both updates.
 */
export const AUTOMATION_CATEGORIES = [
    'briefing-cadence', // "Send Monday briefing at 8am ET", etc.
    'alert-routing', // "Critical alerts → email + SMS", etc.
    'auto-drafts', // "Draft Slack reply when a customer mentions billing in #support", etc.
    'escalation', // "Escalate P0 churn to me immediately + Slack DM", etc.
];
export const AutomationCategory = z.enum(AUTOMATION_CATEGORIES);
/**
 * Non-blank free-text field — trims leading/trailing whitespace, then
 * enforces the length window. Without the `.trim()`, a body of `"   "`
 * passes `min(1)` and gets stored as a blank-looking row in Firestore.
 */
const NonBlankShort = (max) => z.string().trim().min(1).max(max);
export const AutomationRule = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    category: AutomationCategory,
    /** Display name shown in the rule row — inline-editable. */
    name: NonBlankShort(120),
    /** Natural-language "When ..." condition. Free-text for Phase 5; structured DSL is a follow-up. */
    whenCondition: NonBlankShort(500),
    /** Natural-language "Then ..." action. */
    thenAction: NonBlankShort(500),
    enabled: z.boolean().default(true),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdByUid: z.string().min(1),
});
export const ListAutomationRulesResponse = z.object({
    rules: z.array(AutomationRule),
});
/**
 * Wire shape for `POST /api/orgs/:o/automation-rules`. Server stamps
 * `id` / `orgId` / `createdByUid` / `createdAt` / `updatedAt` from the
 * verified token — the client only supplies the operator-authored
 * fields. `enabled` defaults to true via the underlying AutomationRule
 * schema so a create that omits it lights up immediately.
 */
export const CreateAutomationRuleRequest = AutomationRule.omit({
    id: true,
    orgId: true,
    createdAt: true,
    updatedAt: true,
    createdByUid: true,
});
/**
 * Wire shape for `PATCH /api/orgs/:o/automation-rules/:ruleId`. Every
 * field is optional — an empty body is a valid no-op (the server still
 * bumps `updatedAt`). Toggle-only updates pass `{ enabled: false }`;
 * rename-only updates pass `{ name: '…' }`.
 *
 * Explicitly redeclared (rather than `CreateAutomationRuleRequest.partial()`)
 * so the `enabled` field does NOT carry the `true` default through to
 * the patch — a missing `enabled` must round-trip as `undefined`, not
 * `true`. The store handler distinguishes "patch carries `enabled`"
 * from "patch omits `enabled`" via `!== undefined`; an inherited default
 * would silently flip every empty patch's `enabled` to `true`.
 */
export const UpdateAutomationRuleRequest = z.object({
    category: AutomationCategory.optional(),
    name: NonBlankShort(120).optional(),
    whenCondition: NonBlankShort(500).optional(),
    thenAction: NonBlankShort(500).optional(),
    enabled: z.boolean().optional(),
});
